import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Box, Button, Divider, Typography } from '@mui/material';
import Dialog from '../../Dialog';
import { ColumnFlexBox, CenteredFlexBox } from '../../FlexBox';
import useAPI from '../../../api';
import useTranslate from '../../../Hooks/useTranslate';
import {
  pickSavedLayout,
  parseSavedLayout,
} from '../../../Hooks/useSectionLayout';
import { cellsForType } from '../columns/CellRegistry';
import { filtersForType } from '../filters/FilterRegistry';
import { ROW_BY_SURFACE } from './LayoutRows';
import { surfaceKey, catalogueKey } from './surfaceKey';
import { SURFACE_LABELS } from './surfaceLabels';
import EntryDialog from './EntryDialog';
import KeyPicker from './KeyPicker';
import SortableRows, { reordered } from './SortableRows';
import SectionLabel from '../../SectionLabel';
import { NEW_LAYOUT } from './useLayoutEdit';
import useLayoutWrites from './useLayoutWrites';
import LayoutPicker from './LayoutPicker';
import DeleteLayoutButton from './DeleteLayoutButton';

/** The surfaces where showing nothing is a choice, so an empty layout is saveable. */
const EMPTY_ALLOWED = ['search'];

/** What a surface's new entry declares beyond its key and label. */
const SURFACE_SEED = {
  list: (type) => ({ cell: cellsForType(type)[0]?.cell }),
  filters: (type) => ({
    type: filtersForType(type)[0]?.type,
    size: { xs: 12, md: 6 },
  }),
  sorting: () => ({}),
  search: () => ({}),
};

/**
 * A new layout entry for a catalogue key, in the surface's own dialect and seeded from the
 * checked-in layout when it holds that key.
 */
const newEntry = (catalogueEntry, defaultLayout, surface) => {
  const key = surfaceKey(catalogueEntry.key, surface);
  const declared = (defaultLayout ?? []).find((entry) => entry.key === key);

  if (declared) return { ...declared };

  return {
    key,
    label: catalogueEntry.label,
    ...(SURFACE_SEED[surface] ?? SURFACE_SEED.list)(catalogueEntry.type),
  };
};

/**
 * Edits the layout of a section's surface: which columns appear, in what order, rendered by which
 * cell, how wide, and with what vocabulary.
 *
 * Reads the catalogue for what the section can show; saves the layout the lists then render.
 *
 * @param {Object} props - Component props.
 * @param {Boolean} props.open - Whether the dialog is open.
 * @param {Function} props.close - Closes it.
 * @param {String} props.sectionName - The section being configured.
 * @param {String} props.surface - list, detail, filters or form.
 * @param {Array} props.defaultLayout - The layout checked into the client.
 * @param {Array} props.parameters - Runtime values the page publishes to the fields it renders.
 *
 * @returns {React.JSX.Element}
 */
const LayoutEditor = function LayoutEditor({
  open = false,
  close = () => {},
  sectionName,
  surface = 'list',
  defaultLayout = [],
  parameters = [],
}) {
  const intl = useIntl();
  const translate = useTranslate();
  const API = useAPI();
  const { save, remove } = useLayoutWrites();

  const [catalogue, setCatalogue] = useState([]);
  const [entries, setEntries] = useState([]);
  const [layouts, setLayouts] = useState([]);
  const [selectedId, setSelectedId] = useState(NEW_LAYOUT);
  const [name, setName] = useState('');
  const [editingCell, setEditingCell] = useState(null);

  const selected = layouts.find((layout) => layout.id === selectedId) ?? null;

  useEffect(() => {
    if (!open || !sectionName) return;

    API.getColumnCatalogue(
      sectionName,
      { silent: true },
      (res) => setCatalogue(res ?? []),
      () => setCatalogue([])
    );

    // As saved, like the detail editor: an editor writes back what it was given, so it is given the
    // layout rather than the layout with the presets it names merged into it.
    API.getSavedSectionLayouts(
      sectionName,
      surface,
      { silent: true },
      (res) => {
        const rows = res ?? [];
        const row = pickSavedLayout(rows);

        setLayouts(rows);
        setSelectedId(row?.id ?? NEW_LAYOUT);
        setName(row?.name ?? '');
        setEntries(parseSavedLayout(row) ?? defaultLayout ?? []);
      },
      () => {
        setLayouts([]);
        setSelectedId(NEW_LAYOUT);
        setName('');
        setEntries(defaultLayout ?? []);
      }
    );
  }, [open, sectionName, surface]);

  /** Loads another saved layout, or starts a new one from the checked-in default. */
  const selectLayout = (id) => {
    const row = layouts.find((layout) => layout.id === id) ?? null;

    setSelectedId(id);
    setName(row?.name ?? '');
    setEntries(parseSavedLayout(row) ?? defaultLayout ?? []);
  };

  const allowsEmpty = EMPTY_ALLOWED.includes(surface);

  const EntryRow = ROW_BY_SURFACE[surface] ?? ROW_BY_SURFACE.list;

  // Types by key, so a row is handed its own type without searching the catalogue on every render.
  const typesByKey = useMemo(
    () =>
      Object.fromEntries(
        (catalogue ?? []).map((entry) => [entry.key, entry.type])
      ),
    [catalogue]
  );

  const addEntry = (catalogueEntry) =>
    setEntries((current) => [
      ...current,
      newEntry(catalogueEntry, defaultLayout, surface),
    ]);

  // Only one entry can be the default, so setting it clears the flag on the others.
  const updateEntry = useCallback(
    (index, changes) =>
      setEntries((current) =>
        current.map((entry, position) => {
          if (position === index) return { ...entry, ...changes };

          return changes.isDefault && entry.isDefault
            ? { ...entry, isDefault: undefined }
            : entry;
        })
      ),
    []
  );

  const moveEntry = useCallback(
    (from, to) => setEntries((current) => reordered(current, from, to)),
    []
  );

  const removeEntry = useCallback(
    (index) =>
      setEntries((current) =>
        current.filter((_, position) => position !== index)
      ),
    []
  );

  const handleSave = () =>
    save(
      {
        id: selected?.id ?? null,
        sectionName,
        subSectionId: selected?.subSectionId ?? null,
        surface,
        name,
        layout: entries,
      },
      close
    );

  /** Drops the unsaved edits, back to the layout as it was loaded. */
  const handleReset = () => selectLayout(selected ? selected.id : NEW_LAYOUT);

  const handleDelete = () => {
    if (selected) remove(selected.id, sectionName, close);
  };

  return (
    <>
      <Dialog
        id="layout-editor-dialog"
        title={
          <CenteredFlexBox gap="16px">
            <Typography variant="h6" sx={{ fontSize: '24px', flex: 'none' }}>
              {`${intl.messages.EDIT_LAYOUT} — ${translate(
                SURFACE_LABELS[surface] ?? surface
              )}`}
            </Typography>
            <LayoutPicker
              layouts={layouts}
              value={selectedId}
              name={name}
              sx={{ width: '240px', flex: 'none' }}
              onSelect={selectLayout}
              onNameChange={setName}
            />
            <DeleteLayoutButton
              disabled={!selected}
              name={selected?.name ?? ''}
              onDelete={handleDelete}
            />
          </CenteredFlexBox>
        }
        open={open}
        onClose={close}
        onSubmit={handleSave}
        submitLabel="SAVE"
        disableSubmit={entries.length === 0 && !allowsEmpty}
        width="lg"
        fullWidth
        dividers
        leftAction={
          <Button id="reset-layout-btn" color="inherit" onClick={handleReset}>
            {intl.messages.DISCARD_CHANGES}
          </Button>
        }
      >
        <ColumnFlexBox sx={{ flex: 1, minWidth: 0, height: '65vh' }} gap="12px">
          <Box sx={{ display: 'flex', gap: '24px', flex: 1, minHeight: 0 }}>
            <ColumnFlexBox
              sx={{ width: '280px', flex: 'none', minHeight: 0 }}
              gap="8px"
            >
              <SectionLabel>{intl.messages.AVAILABLE}</SectionLabel>
              <KeyPicker
                catalogue={catalogue}
                entries={entries}
                surface={surface}
                defaultLayout={defaultLayout}
                onPick={addEntry}
              />
            </ColumnFlexBox>
            <Divider orientation="vertical" flexItem />
            <ColumnFlexBox
              sx={{ flex: 1, minWidth: 0, minHeight: 0 }}
              gap="8px"
            >
              <SectionLabel>
                {translate(SURFACE_LABELS[surface] ?? surface)}
              </SectionLabel>
              {entries.length === 0 && allowsEmpty && (
                <Typography variant="body2" sx={{ opacity: '70%' }}>
                  {intl.messages.SEARCH_BAR_HIDDEN}
                </Typography>
              )}
              <SortableRows items={entries} onReorder={moveEntry}>
                {(entry, index) => (
                  <EntryRow
                    entry={entry}
                    index={index}
                    type={typesByKey[catalogueKey(entry.key, surface)]}
                    onChange={updateEntry}
                    onRemove={removeEntry}
                    onConfigure={setEditingCell}
                  />
                )}
              </SortableRows>
            </ColumnFlexBox>
          </Box>
        </ColumnFlexBox>
      </Dialog>
      <EntryDialog
        open={editingCell !== null}
        close={() => setEditingCell(null)}
        entry={entries[editingCell] ?? null}
        surface={surface}
        catalogue={catalogue}
        parameters={parameters}
        // What a condition or a dependency may name: the entries beside this one, never itself.
        siblings={entries.filter((_, at) => at !== editingCell)}
        onConfirm={(changes) => updateEntry(editingCell, changes)}
      />
    </>
  );
};

export default LayoutEditor;
